import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '@/core/store/store';
import { setError, clearError } from '@/core/store/slices/errorSlice';
import { updateTokens, logout } from '@/features/auth/store/authSlice';
import type { 
  ApiError, 
  RetryableAxiosRequestConfig 
} from '@/core/types/apiTypes';
import { notifyError, notifySuccess, notifyWarning } from '@/core/utils/toastUtils';

// Helper function to force logout and redirect
const forceLogout = () => {
  // Clear tokens from localStorage as a backup
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  
  // Dispatch logout action
  store.dispatch(logout());
  
  // Force redirect to login page
  window.location.href = '/login';
};

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Variables to track refresh state and pending requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Process the queued requests after token refresh
const processQueue = (error: Error | null, token: string | null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor - add auth token, clear previous errors, and handle FormData
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    store.dispatch(clearError());

    const token = store.getState().auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData submissions properly
    if (config.data instanceof FormData) {
      // Remove Content-Type header to let the browser set it with the boundary
      delete config.headers['Content-Type'];
      
      // Prevent axios from trying to JSON.stringify the FormData
      config.transformRequest = [(data) => data];
      
      // Log FormData content for debugging if needed
      // if (import.meta.env.DEV) {
      //   for (const pair of config.data.entries()) {
      //     const value = pair[1] instanceof File 
      //       ? `File: ${(pair[1] as File).name} (${(pair[1] as File).type}, ${Math.round((pair[1] as File).size / 1024)} KB)`
      //       : pair[1];
      //     console.log(`${pair[0]}: ${value}`);
      //   }
      // }
    }
    
    return config;
  },
  (error) => {
    notifyError('Failed to setup request');
    return Promise.reject(error);
  }
);

// Response interceptor - handle success messages and errors
apiClient.interceptors.response.use(
  (response) => {
    if (response.data?.message && typeof response.data.message === 'string') {
      notifySuccess(response.data.message);
    }
    
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    // Network errors
    if (!error.response) {
      const networkError = {
        message: 'Network error - please check your connection',
        status: 500
      };
      notifyError(networkError.message);
      store.dispatch(setError(networkError));
      return Promise.reject(networkError);
    }

    // Check for specific token validation errors
    const isTokenNotValidError = error.response.data?.type === 'client_error' && 
                               error.response.data?.errors?.some(err => err.code === 'token_not_valid');
    
    if (isTokenNotValidError) {
      // Force logout and redirect
      notifyWarning('Session expired - please log in again');
      forceLogout();
      return Promise.reject(new Error('Session expired - please log in again'));
    }

    // Token refresh logic - only for standard 401 errors
    if (error.response.status === 401 && originalRequest && !originalRequest._retry) {
      // If already refreshing, add to queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest && typeof originalRequest.headers === 'object') {
              originalRequest.headers.Authorization = `Bearer ${token as string}`;
            }
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState().auth.refreshToken;
      
      if (!refreshToken) {
        isRefreshing = false;
        forceLogout();
        return Promise.reject(new Error('No refresh token available'));
      }

      try {
        const response = await apiClient.post('/token/refresh/', { refresh: refreshToken });
        const newToken = response.data.access;
        
        // Use the proper updateTokens action from authSlice
        store.dispatch(updateTokens({
          access: newToken,
          refresh: response.data.refresh || refreshToken
        }));

        // Process any requests that were waiting
        processQueue(null, newToken);

        if (originalRequest && typeof originalRequest.headers === 'object') {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        isRefreshing = false;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Direct handling for token refresh errors - force logout
        processQueue(new Error('Session expired'), null);
        notifyWarning('Session expired - please log in again');
        forceLogout();
        
        isRefreshing = false;
        return Promise.reject(new Error('Session expired - please log in again'));
      }
    }

    // Format error response for standard errors
    const errorMessage = error.response.data?.errors?.[0]?.detail || 
                        error.response.data?.message || 
                        'An error occurred';
      
    const errorResponse: ApiError = {
      message: errorMessage,
      status: error.response.status,
      type: error.response.data?.type,
      errors: error.response.data?.errors
    };

    store.dispatch(setError(errorResponse));

    // Show error notification
    if (error.response.data?.type === 'validation_error') {
      notifyError('Validation Error');
    } else {
      notifyError(errorResponse.message);
    }

    return Promise.reject(errorResponse);
  }
);

export default apiClient;